import * as contactApi from "@rentiohq/shared-frontend/dist/redux/contact/contact.api";
import {
  IContact,
  IContactExternalInfo,
} from "@rentiohq/shared-frontend/dist/types/contact.types";
import { useEffect, useRef, useState } from "react";

type TExternalInfoByAccountId = {
  [accountId: number]: IContactExternalInfo | undefined;
};

/**
 * Looks up the KBO/BCE company data for every contact that could become the
 * payer of a Banqup rent deposit, keyed by account id so the KYB step can match
 * it to the payer once one is selected.
 *
 * Each contact is looked up once. `isReady` flips once every lookup has settled,
 * and the form has to wait for it: the step schemas are registered with the
 * form store when the form mounts, so company data that arrives afterwards
 * never reaches the field defaults.
 *
 * The register is a third party, so a failed lookup counts as settled and just
 * leaves the company fields empty for the broker to fill in manually.
 */
export const useCompanyExternalInfo = (contacts: IContact[]) => {
  const [externalInfo, setExternalInfo] = useState<TExternalInfoByAccountId>(
    {},
  );
  const [settledContactIds, setSettledContactIds] = useState<number[]>([]);
  // A ref rather than state: requesting a lookup must not itself schedule a
  // render, otherwise the effect re-runs before the first request resolves.
  const requestedContactIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    const contactsToFetch = contacts.filter(
      contact => !requestedContactIds.current.has(contact.id),
    );
    if (contactsToFetch.length === 0) {
      return;
    }

    contactsToFetch.forEach(async contact => {
      requestedContactIds.current.add(contact.id);

      let info: IContactExternalInfo | undefined;

      try {
        info = await contactApi.getContactExternalInfo(contact.id);
      } catch {
        info = undefined;
      }

      setExternalInfo(current => ({
        ...current,
        [contact.accountId]: info,
      }));
      setSettledContactIds(current => [...current, contact.id]);
    });
  }, [contacts]);

  const isReady = contacts.every(contact =>
    settledContactIds.includes(contact.id),
  );

  return { companyExternalInfo: externalInfo, isReady };
};
